import React, { ReactNode } from 'react';
import { Alert, Box, Button, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import { ChatErrorBoundaryProps } from '../../types/assistant';
import classes from './ChatErrorBoundary.module.css';

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ChatErrorBoundary Component
 * React Error Boundary for catching and displaying errors in message rendering
 */
export class ChatErrorBoundary extends React.Component<ChatErrorBoundaryProps, State> {
  constructor(props: ChatErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static displayName = 'ChatErrorBoundary';

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error information
    console.error('ChatErrorBoundary caught an error:', error, errorInfo);

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box className={classes.errorContainer}>
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Failed to render message"
            color="red"
            variant="light"
          >
            <Stack gap="sm">
              <Text size="sm">
                An error occurred while rendering this message. Please try again.
              </Text>

              {import.meta.env.DEV && this.state.error && (
                <Box className={classes.errorDetails}>
                  <Text size="xs" c="dimmed" component="div">
                    <strong>Error:</strong> {this.state.error.message}
                  </Text>
                </Box>
              )}

              <Button
                size="sm"
                onClick={this.handleReset}
                leftSection={<IconRefresh size={14} />}
                variant="light"
              >
                Try Again
              </Button>
            </Stack>
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}
